import {
  createSmsTextSchema,
  getSmsSegmentCount,
  smsMaxSegments,
} from '@minty/shared/sms';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Send, Wand2 } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { ReactNode } from 'react';
import type { Id } from '@minty/shared/ids';
import { useCan } from '~/hooks/use-can';
import { Button } from '~/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { useMessageSuggestion } from '~/hooks/use-message-suggestion';
import { appErrorCode } from '~/lib/app-error';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

function SmsComposerContent({
  contactId,
  phoneNumber,
  header,
  onSent,
}: {
  contactId: Id<'contacts'>;
  phoneNumber: string;
  header: ReactNode;
  onSent?: () => void;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: sendSms } = useMutation(
    trpc.sms.outbound.send.mutationOptions(),
  );
  const suggestion = useMessageSuggestion('sms', {
    contactId,
    answeringEmailId: null,
  });
  const clientRequestIdRef = useRef<string | null>(null);
  const { data: flags } = useQuery(
    trpc.config.queries.getFeatureFlags.queryOptions(),
  );
  const maxSegments = smsMaxSegments({ longerSms: flags?.longerSms ?? false });
  const schema = useMemo(
    () =>
      z.object({
        text: createSmsTextSchema(maxSegments, {
          emptyMessage: t('Message is required.'),
          tooLongMessage: t('Message is too long.'),
        }),
      }),
    [t, maxSegments],
  );
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { text: '' },
  });
  const isSubmitting = form.formState.isSubmitting;
  const isBusy = isSubmitting || suggestion.isGenerating;
  let submitIcon = <Send className="size-4" strokeWidth={2.5} />;

  if (isSubmitting) {
    submitIcon = <Loader2 className="size-4 animate-spin" strokeWidth={2.5} />;
  }

  let generateIcon = <Wand2 className="size-4" strokeWidth={2.5} />;

  if (suggestion.isGenerating) {
    generateIcon = (
      <Loader2 className="size-4 animate-spin" strokeWidth={2.5} />
    );
  }

  const handleGenerate = async () => {
    const draft = await suggestion.generate(form.getValues('text'));

    if (draft === null) {
      return;
    }

    form.setValue('text', draft, { shouldDirty: true, shouldValidate: true });
  };

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    try {
      const clientRequestId =
        clientRequestIdRef.current ?? globalThis.crypto.randomUUID();
      clientRequestIdRef.current = clientRequestId;

      await sendSms({
        contactId,
        text: values.text,
        clientRequestId,
      });
      clientRequestIdRef.current = null;
      form.reset({ text: '' });
      onSent?.();
      notify.success(t('SMS queued for sending.'));
    } catch (error) {
      const reason = appErrorCode(error);

      notify.error(
        reason === 'SMS is not configured'
          ? t('SMS is not configured')
          : reason === 'Contact has no phone number'
            ? t('Contact has no phone number')
            : t('Unable to send SMS.'),
      );
    }
  };

  return (
    <Form.Root
      form={form}
      onSubmit={handleSubmit}
      submitMode="explicit"
      aria-busy={isBusy}
      className="flex flex-col bg-background px-4 pb-4 pt-3"
    >
      <fieldset disabled={isBusy} className="contents">
        <div className="flex flex-col gap-2 pb-2">
          {header}
          <Input value={phoneNumber} readOnly aria-label={t('SMS recipient')} />
        </div>

        <FormField
          control={form.control}
          name="text"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2 pt-3">
              <FormControl>
                <Textarea
                  {...field}
                  disabled={isBusy}
                  placeholder={t('Message')}
                  className="max-h-(--composer-max-height) min-h-32 resize-none rounded-md border-border px-4 py-3 text-sm leading-6"
                />
              </FormControl>
              <div className="flex min-h-5 items-center justify-between gap-3">
                <FormMessage />
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {t('{{count}} of {{max}} SMS segments', {
                    count: getSmsSegmentCount(field.value),
                    max: maxSegments,
                  })}
                </span>
              </div>
            </FormItem>
          )}
        />
      </fieldset>

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="muted"
          size="icon-sm"
          onClick={handleGenerate}
          disabled={isBusy}
          aria-label={t('Generate AI response', { ns: 'mailbox' })}
        >
          {generateIcon}
        </Button>

        <Button type="submit" disabled={isBusy} className="gap-2">
          {submitIcon}
          {t('Send SMS')}
        </Button>
      </div>
    </Form.Root>
  );
}

export function SmsComposer(props: Parameters<typeof SmsComposerContent>[0]) {
  const allowed = useCan('reception');
  return allowed ? <SmsComposerContent {...props} /> : null;
}
